import { supabase } from '@/lib/supabaseClient';
import { updateJob, formatUsd, parseContractorDate } from '@/lib/jobsData';
import {
  STORE_REBATES,
  STORES,
  loadStoreRebateRates,
  getProXtraConfig,
  computeProXtraProgress,
} from '@/lib/receiptsData';

export {
  formatUsd,
  STORE_REBATES,
  STORES,
  loadStoreRebateRates,
  getProXtraConfig,
  computeProXtraProgress,
};

/* ----------------------------------------------------------------------------
 * Rebate status lifecycle (Phase 1 — strict one-directional flow)
 *
 * Canonical IDs (in order):
 *   logged     — receipt saved/on file internally (auto-assigned on save)
 *   submitted  — exported + mailed to the store
 *   received   — voucher / cash back received from store
 *
 * Legacy data may carry `rebate_status: 'pending'`. Those rows are silently
 * normalized to `logged` on read (see `normalizeStatus`). They get persisted
 * with the new canonical id on the first mutation through advance/undo.
 * ------------------------------------------------------------------------- */
export const REBATE_STATUSES = ['logged', 'submitted', 'received'];

/** Forward transitions: only the immediate next stage. */
export const REBATE_NEXT_STATUS = {
  logged: 'submitted',
  submitted: 'received',
  received: null,
};

/** Reverse transitions: only the immediate previous stage. */
export const REBATE_PREV_STATUS = {
  logged: null,
  submitted: 'logged',
  received: 'submitted',
};

export const REBATE_STATUS_LABEL = {
  logged: 'Logged',
  submitted: 'Submitted',
  received: 'Cash Back Received',
};

export const REBATE_STATUS_TONE = {
  logged: 'warning',
  submitted: 'default',
  received: 'success',
};

/**
 * Normalize a raw value pulled from `receipt.rebate_status` into the canonical
 * id set. Treats undefined / null / legacy `pending` as `logged`.
 */
export const normalizeStatus = (raw) => {
  const v = String(raw || '').toLowerCase();
  if (v === 'submitted') return 'submitted';
  if (v === 'received') return 'received';
  // Everything else — including legacy `pending`, empty, unknown — falls back
  // to `logged` per the new flow's "auto-assign on save" rule.
  return 'logged';
};

/* ----------------------------------------------------------------------------
 * Quarter helpers
 * ------------------------------------------------------------------------- */
export const dateToQuarter = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : parseContractorDate(date);
  if (!d) return null;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
};

export const QUARTER_OPTIONS = (() => {
  const out = [{ value: 'all', label: 'All time' }];
  const now = new Date();
  for (let i = 0; i < 8; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const label = dateToQuarter(d);
    if (label && !out.find((o) => o.value === label)) {
      out.push({ value: label, label });
    }
  }
  return out;
})();

/* ----------------------------------------------------------------------------
 * Fetch + flatten rebate-bearing receipts
 * ------------------------------------------------------------------------- */
const JOB_FIELDS =
  'id,name,client,status,receipts,created_at';

export const fetchRebatesData = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Each receipt becomes a rebate row IF the store is rebate-eligible.
 */
export const flattenRebates = (jobs = []) => {
  const rows = [];
  for (const job of jobs) {
    const receipts = Array.isArray(job?.receipts) ? job.receipts : [];
    receipts.forEach((r, idx) => {
      const rate = STORE_REBATES[r?.store] ?? Number(r?.rebate_rate) ?? 0;
      if (!rate || rate <= 0) return; // skip non-rebate stores
      const date = parseContractorDate(r?.date);
      const rebate =
        Number.isFinite(Number(r?.rebate)) && Number(r?.rebate) > 0
          ? Number(r.rebate)
          : Math.round((Number(r?.amount) || 0) * rate * 100) / 100;
      rows.push({
        row_key: `${job.id}-${r?.id || 'noid'}-${idx}`,
        receipt_id: r?.id || null,
        idx,
        job_id: job.id,
        job_name: job.name || 'Untitled job',
        client: job.client || '',
        store: r?.store || 'Other',
        amount: Number(r?.amount) || 0,
        subtotal: Number(r?.subtotal) || null,
        tax: Number(r?.tax) || null,
        rebate,
        rebate_rate: rate,
        rebate_number: r?.rebate_number || null,
        rebate_status: normalizeStatus(r?.rebate_status),
        rebate_logged_at:
          r?.rebate_logged_at || r?.created_at || null,
        rebate_submitted_at: r?.rebate_submitted_at || null,
        rebate_received_at: r?.rebate_received_at || null,
        rebate_batch_id: r?.rebate_batch_id || null,
        date: r?.date || '',
        date_obj: date,
        quarter: dateToQuarter(date) || dateToQuarter(r?.created_at) || 'Unscheduled',
        desc: r?.desc || '',
      });
    });
  }
  rows.sort((a, b) => {
    const ta = a.date_obj ? a.date_obj.getTime() : 0;
    const tb = b.date_obj ? b.date_obj.getTime() : 0;
    return tb - ta;
  });
  return rows;
};

/* ----------------------------------------------------------------------------
 * Stats / aggregations
 *
 * Phase 1 mapping:
 *   - "Money you're owed" card = LOGGED + SUBMITTED totals (= `owed`)
 *   - "Received" card           = CASH BACK RECEIVED total (= `received`)
 *   - Recovery rate             = received / total
 * ------------------------------------------------------------------------- */
export const computeRebateStats = (rows = []) => {
  let total = 0;
  let owed = 0; // logged + submitted = not yet received
  let received = 0;
  let logged = 0;
  let submitted = 0;
  let loggedCount = 0;
  let submittedCount = 0;
  const byStore = {};
  const byJob = {};
  for (const r of rows) {
    total += r.rebate;
    if (r.rebate_status === 'received') received += r.rebate;
    if (r.rebate_status !== 'received') owed += r.rebate;
    if (r.rebate_status === 'logged') {
      logged += r.rebate;
      loggedCount += 1;
    }
    if (r.rebate_status === 'submitted') {
      submitted += r.rebate;
      submittedCount += 1;
    }

    if (!byStore[r.store]) byStore[r.store] = { store: r.store, total: 0, count: 0, rate: r.rebate_rate };
    byStore[r.store].total += r.rebate;
    byStore[r.store].count += 1;

    if (!byJob[r.job_id])
      byJob[r.job_id] = {
        job_id: r.job_id,
        job_name: r.job_name,
        client: r.client,
        total: 0,
        count: 0,
      };
    byJob[r.job_id].total += r.rebate;
    byJob[r.job_id].count += 1;
  }
  return {
    count: rows.length,
    total,
    owed,
    received,
    logged,
    submitted,
    loggedCount,
    submittedCount,
    // Back-compat: some older consumers may still read `pending`. Keep it as
    // an alias of the new `logged` bucket so nothing crashes during rollout.
    pending: logged,
    byStore: Object.values(byStore).sort((a, b) => b.total - a.total),
    byJob: Object.values(byJob).sort((a, b) => b.total - a.total),
  };
};

/* ----------------------------------------------------------------------------
 * Filter
 * ------------------------------------------------------------------------- */
export const applyRebateFilter = (
  rows = [],
  { search = '', store = 'all', status = 'all', quarter = 'all', batch = 'all' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  // Back-compat: callers passing the old `pending` token are treated as `logged`.
  const statusKey = status === 'pending' ? 'logged' : status;
  return rows.filter((r) => {
    if (store !== 'all' && r.store !== store) return false;
    if (statusKey !== 'all' && r.rebate_status !== statusKey) return false;
    if (quarter !== 'all' && r.quarter !== quarter) return false;
    if (batch !== 'all' && r.rebate_batch_id !== batch) return false;
    if (!q) return true;
    const hay = [r.job_name, r.client, r.store, r.desc]
      .map((v) => (v || '').toLowerCase())
      .join(' | ');
    return hay.includes(q);
  });
};

/* ----------------------------------------------------------------------------
 * Writes — patch the rebate_status of the matching receipt inside jobs.receipts
 *
 * Phase 1 contract:
 *   - All status changes go through the guarded helpers below.
 *   - `setRebateStatus(job, receiptId, status)` writes a status verbatim AFTER
 *     validating the transition is allowed (one-stage forward OR backward).
 *   - `advanceRebate(job, receiptId)` moves logged→submitted or submitted→received.
 *   - `undoRebate(job, receiptId)`    moves submitted→logged or received→submitted.
 *   - Skipping stages (e.g. logged→received) is rejected.
 *   - The current status read from the receipt is normalized first, so legacy
 *     `pending` rows transparently behave as `logged`.
 * ------------------------------------------------------------------------- */

/** Internal — locate a receipt by id inside a job's `receipts[]`. */
const findReceipt = (job, receiptId) => {
  const list = Array.isArray(job?.receipts) ? job.receipts : [];
  return list.find((r) => r?.id === receiptId) || null;
};

/** Public — current normalized status of a given receipt on a given job. */
export const getCurrentStatus = (job, receiptId) => {
  const r = findReceipt(job, receiptId);
  return r ? normalizeStatus(r.rebate_status) : null;
};

/**
 * Validate a transition. Allows either:
 *   - one-stage advance (current -> REBATE_NEXT_STATUS[current])
 *   - one-stage undo    (current -> REBATE_PREV_STATUS[current])
 * Returns `null` if valid, otherwise an Error.
 */
const validateTransition = (current, next) => {
  if (!REBATE_STATUSES.includes(next)) {
    return new Error('Invalid rebate status');
  }
  if (current === next) return null; // idempotent — treat as no-op
  if (REBATE_NEXT_STATUS[current] === next) return null;
  if (REBATE_PREV_STATUS[current] === next) return null;
  return new Error(
    `Illegal rebate transition: ${current} -> ${next}. Only one-stage moves are allowed.`
  );
};

/**
 * Low-level write. Performs the actual mutation on jobs.receipts and the
 * timestamp side-effects. Callers SHOULD prefer advanceRebate / undoRebate
 * but this is exported for explicit transitions that have already been
 * validated by the caller.
 */
export const setRebateStatus = async (job, receiptId, status) => {
  if (!job?.id) throw new Error('Missing job id');
  const target = String(status || '').toLowerCase();
  if (!REBATE_STATUSES.includes(target)) {
    throw new Error('Invalid rebate status');
  }
  const current = getCurrentStatus(job, receiptId) || 'logged';
  const err = validateTransition(current, target);
  if (err) throw err;

  const nowIso = new Date().toISOString();
  const receipts = (Array.isArray(job.receipts) ? job.receipts : []).map((r) => {
    if (r?.id !== receiptId) return r;
    const next = { ...r, rebate_status: target };
    // Persist transition timestamps on first-time forward moves only — they
    // form an audit trail for CSV export. Undo does NOT clear them.
    if (target === 'logged' && !r?.rebate_logged_at)
      next.rebate_logged_at = nowIso;
    if (target === 'submitted' && !r?.rebate_submitted_at)
      next.rebate_submitted_at = nowIso;
    if (target === 'received' && !r?.rebate_received_at)
      next.rebate_received_at = nowIso;
    // Back to logged = not in any mailed batch any more.
    if (target === 'logged') delete next.rebate_batch_id;
    return next;
  });
  return updateJob(job.id, { receipts });
};

/**
 * Move a rebate one stage forward. Throws on illegal moves (e.g. when the
 * receipt is already at the terminal `received` stage).
 */
export const advanceRebate = async (job, receiptId) => {
  const current = getCurrentStatus(job, receiptId);
  if (!current) throw new Error('Receipt not found');
  const next = REBATE_NEXT_STATUS[current];
  if (!next) {
    throw new Error('Rebate is already at the final stage.');
  }
  return setRebateStatus(job, receiptId, next);
};

/**
 * Move a rebate exactly one stage backward. Throws on illegal moves
 * (e.g. attempting to undo a `logged` receipt).
 */
export const undoRebate = async (job, receiptId) => {
  const current = getCurrentStatus(job, receiptId);
  if (!current) throw new Error('Receipt not found');
  const prev = REBATE_PREV_STATUS[current];
  if (!prev) {
    throw new Error('Nothing to undo — rebate is already at the first stage.');
  }
  return setRebateStatus(job, receiptId, prev);
};

/* ----------------------------------------------------------------------------
 * Batches — "I mailed this stack of rebates" in one move
 *
 * John (Oct 6): "how do I indicate that I have sent in a batch of rebates —
 * and reset the tracker back to zero?" Marking a batch sent moves every
 * selected `logged` rebate to `submitted` with ONE sent date (the day he
 * mailed it, which he can pick) and one `rebate_batch_id`, so the batch can
 * be found, received, or undone together later. "Ready to send" (= logged)
 * drops to $0; "owed" doesn't, because the store still owes the money.
 * ------------------------------------------------------------------------- */

/** RB-20261006-7f3a — date for humans, suffix so two batches a day differ. */
export const makeRebateBatchId = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const suffix = Math.random().toString(16).slice(2, 6).padEnd(4, '0');
  return `RB-${ymd}-${suffix}`;
};

/** A picked calendar day ("2026-10-06") as an ISO timestamp at local noon. */
export const dayToIso = (day) => {
  if (!day) return new Date().toISOString();
  const d = new Date(`${day}T12:00:00`);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

/**
 * What a batch action would touch: rows already in `fromStatus` that can be
 * written (a receipt with no id can't be found again to update — those are
 * counted, never silently dropped).
 */
export const summarizeBatch = (rows = [], fromStatus) => {
  const eligible = rows.filter((r) => r.rebate_status === fromStatus);
  const movable = eligible.filter((r) => r.receipt_id);
  const byStore = {};
  let total = 0;
  for (const r of movable) {
    total += r.rebate;
    if (!byStore[r.store]) byStore[r.store] = { store: r.store, count: 0, total: 0 };
    byStore[r.store].count += 1;
    byStore[r.store].total += r.rebate;
  }
  return {
    rows: movable,
    count: movable.length,
    total: Math.round(total * 100) / 100,
    byStore: Object.values(byStore).sort((a, b) => b.total - a.total),
    unmovable: eligible.length - movable.length,
  };
};

/** Every batch that's been marked sent, newest first. */
export const listRebateBatches = (rows = []) => {
  const map = {};
  for (const r of rows) {
    if (!r.rebate_batch_id) continue;
    const b =
      map[r.rebate_batch_id] ||
      (map[r.rebate_batch_id] = {
        id: r.rebate_batch_id,
        sent_at: r.rebate_submitted_at || null,
        count: 0,
        total: 0,
        waiting: 0,
        stores: new Set(),
      });
    b.count += 1;
    b.total += r.rebate;
    if (r.rebate_status === 'submitted') b.waiting += 1;
    b.stores.add(r.store);
    if (r.rebate_submitted_at && (!b.sent_at || r.rebate_submitted_at < b.sent_at)) {
      b.sent_at = r.rebate_submitted_at;
    }
  }
  return Object.values(map)
    .map((b) => ({ ...b, total: Math.round(b.total * 100) / 100, stores: [...b.stores].sort() }))
    .sort((a, b) => String(b.sent_at || '').localeCompare(String(a.sent_at || '')));
};

/**
 * Move many rebates one stage at once (sent, received, or a whole batch
 * undone back to logged). Each job's receipts are re-read fresh right before
 * the write so a receipt logged a minute ago on another device isn't
 * clobbered by this page's older copy. A receipt only moves if it is still
 * in the expected prior stage — same one-stage rule as the single buttons.
 *
 * Resolves { jobs: [updated job rows], moved, skipped, failed: [{job_id, error}] }.
 */
export const setRebateStatusBatch = async (rows = [], target, { at = null, batchId = null } = {}) => {
  const from =
    target === 'submitted' ? 'logged' : target === 'received' ? 'submitted' : target === 'logged' ? 'submitted' : null;
  if (!from) throw new Error('Invalid rebate status');
  const stamp = at || new Date().toISOString();

  const byJob = new Map();
  for (const r of rows) {
    if (!r?.job_id || !r?.receipt_id) continue;
    if (!byJob.has(r.job_id)) byJob.set(r.job_id, new Set());
    byJob.get(r.job_id).add(r.receipt_id);
  }
  if (!byJob.size) return { jobs: [], moved: 0, skipped: rows.length, failed: [] };

  const { data: fresh, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .in('id', [...byJob.keys()]);
  if (error) throw error;

  const out = { jobs: [], moved: 0, skipped: 0, failed: [] };
  for (const job of fresh || []) {
    const ids = byJob.get(job.id);
    let movedHere = 0;
    const receipts = (Array.isArray(job.receipts) ? job.receipts : []).map((r) => {
      if (!ids.has(r?.id)) return r;
      if (normalizeStatus(r.rebate_status) !== from) return r;
      movedHere += 1;
      const next = { ...r, rebate_status: target };
      if (target === 'submitted') {
        next.rebate_submitted_at = stamp;
        if (batchId) next.rebate_batch_id = batchId;
      } else if (target === 'received') {
        next.rebate_received_at = stamp;
      } else if (target === 'logged') {
        delete next.rebate_batch_id;
      }
      return next;
    });
    out.skipped += ids.size - movedHere;
    if (!movedHere) continue;
    try {
      out.jobs.push(await updateJob(job.id, { receipts }));
      out.moved += movedHere;
    } catch (e) {
      out.failed.push({ job_id: job.id, error: e?.message || String(e) });
    }
  }
  // Jobs that vanished between load and write.
  const seen = new Set((fresh || []).map((j) => j.id));
  for (const [jobId, ids] of byJob) if (!seen.has(jobId)) out.skipped += ids.size;
  return out;
};

/* ----------------------------------------------------------------------------
 * CSV export
 * ------------------------------------------------------------------------- */
export const rebatesToCsv = (rows = []) => {
  const header = [
    'Date',
    'Quarter',
    'Store',
    'Rate %',
    'Amount',
    'Rebate',
    'Rebate #',
    'Status',
    'Submitted',
    'Received',
    'Batch',
    'Job',
    'Client',
    'Description',
  ];
  const esc = (v) => {
    const s = String(v == null ? '' : v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.date,
        r.quarter,
        r.store,
        Math.round(r.rebate_rate * 100) + '%',
        r.amount.toFixed(2),
        r.rebate.toFixed(2),
        r.rebate_number || '',
        REBATE_STATUS_LABEL[r.rebate_status] || r.rebate_status,
        r.rebate_submitted_at || '',
        r.rebate_received_at || '',
        r.rebate_batch_id || '',
        r.job_name,
        r.client,
        r.desc,
      ]
        .map(esc)
        .join(',')
    );
  }
  return lines.join('\n');
};

export const downloadCsv = (filename, csvContent) => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    // noop
  }
};
